// AI Generated: Capabilities component with auto-rotating carousel (node-id=1363-12239)
import React, { useState, useCallback, useEffect } from 'react';
import styles from '$styles/ind/home/capabilities.module.scss';

interface ICapabilitiesProps {
  isMobile?: boolean;
  isDesktop?: boolean;
}

interface Product {
  id: number;
  logo: string;
  title: string;
  name: string;
  description: string;
  dashboardImage: string;
}

const Capabilities: React.FC<ICapabilitiesProps> = () => {
  const [activeProductIndex, setActiveProductIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const products: Product[] = [
    {
      id: 1,
      logo: '/public/images/logos/revmax.png',
      title: 'Automate pricing and maximise revenue',
      name: 'revMax',
      description:
        'revMax is a dynamic pricing tool that automatically sets seat fares using real-time demand and market trends to maximize operator revenue',
      dashboardImage: '/public/images/benefits/dashboard-1.png',
    },
    {
      id: 2,
      logo: '/public/images/logos/redpro.png',
      title: 'Manage, promote, and grow your bus business',
      name: 'redPro',
      description:
        'redPro is a smart platform that helps bus operators manage routes, pricing, and performance efficiently with tech-driven tools',
      dashboardImage: '/public/images/benefits/dashboard-2.png',
    },
    {
      id: 3,
      logo: '/public/images/logos/nova.png',
      title: 'Buy best quality bus parts at better prices',
      name: 'NOVA',
      description:
        'NOVA is a fleet marketplace connecting bus operators with trusted vendors for quality parts at the best prices',
      dashboardImage: '/public/images/benefits/dashboard-3.png',
    },
  ];

  // Auto-rotate through products every 4 seconds
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setActiveProductIndex((current) => (current + 1) % products.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, products.length]);

  const handleProductClick = useCallback((index: number) => {
    setActiveProductIndex(index);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  const activeProduct = products[activeProductIndex];

  return (
    <section 
      className={styles.capabilities} 
      id="capabilities"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.sectionTitle}>
            redBus capabilities powering India's online bus business
          </h2>
          <p className={styles.sectionSubtitle}>
            Powerful tools that will help your business reach milestones
          </p>
        </div>

        {/* Product Slider Section */}
        <div className={styles.productSection}>
          {/* Left Section - Product Cards */}
          <div className={styles.leftSection}>
            {products.map((product, index) => (
              <div
                key={product.id}
                className={`${styles.productCard} ${
                  index === activeProductIndex ? styles.activeCard : ''
                }`}
                onClick={() => handleProductClick(index)}
                role="button"
                tabIndex={0}
                aria-label={`View ${product.name} details`}
                aria-pressed={index === activeProductIndex}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleProductClick(index);
                  }
                }}
              >
                <div className={styles.cardBody}>
                  <img
                    src={product.logo}
                    alt={product.name}
                    className={styles.productLogo}
                  />
                  <h3 className={styles.productTitle}>{product.title}</h3>
                </div>
                {index === activeProductIndex && (
                  <div className={styles.cardArrow} />
                )}
                {index === activeProductIndex && !isPaused && (
                  <div className={styles.progressBar} />
                )}
              </div>
            ))}
          </div>

          {/* Right Section - Slider */}
          <div className={styles.rightSection}>
            <div className={styles.sliderContainer}>
              <div className={styles.previewImage}>
                <img
                  src={activeProduct.dashboardImage}
                  alt={`${activeProduct.name} Dashboard`}
                  className={styles.dashboardImage}
                  key={activeProduct.id}
                />
              </div>
              <div className={styles.productContent}>
                <h4 className={styles.productName}>{activeProduct.name}</h4>
                <p className={styles.productDescription}>{activeProduct.description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Capabilities;
// AI Generated: End of capabilities component


